import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead,
  TableRow, Alert, CircularProgress, Chip, Accordion, AccordionSummary, AccordionDetails, Button,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import { formatCurrency } from '../../utils/format';
import LoanSlipVerifyDialog from '../dialogs/LoanSlipVerifyDialog';

// รายการ Expense ของ customer หนึ่งฝั่ง ที่ถูกจัดเข้า "โครงการ" พิเศษสำหรับบันทึก
// ยืม/คืนเงินโดยเฉพาะ (ไม่เกี่ยวกับ OCR สมุดจด) — ใช้ทั้งฝั่งยืมและฝั่งคืนของคู่เดียวกัน
// จัดกลุ่มรายการ (flat expense rows จาก backend) ตาม payment_id เอง ฝั่ง client
// แสดงเป็น accordion ต่อ Payment หนึ่งใบ (เหมือน CustomerPaymentAccordionList)
//
// showStatusColumn: โชว์สถานะ "ยืนยันแล้ว/ยังไม่ยืนยัน" เป็น hybrid ของสองเงื่อนไข:
// (1) payment ที่เกิดก่อน dateFrom (opening_balance_date) ถือว่า "ยืนยันแล้ว" อัตโนมัติ
// เพราะถูกนับรวมอยู่ในยอดคงเหลือไปแล้ว (2) payment ที่เกิดหลัง dateFrom ต้องเช็ค field
// จริง payment.ocr_verified_loan_pair (ยังไม่มีระบบ OCR อัตโนมัติมา set ค่านี้ให้ตอนนี้
// — ค้าง false ไว้ก่อน)
//
// onTotal: ยอดที่ "รวม" ท้าย list เอง (เห็นในการ์ดนี้ตรงๆ) ยังคงนับรายการทั้งหมดเสมอ
// (สะสมทั้งหมด ไม่เกี่ยวกับ dateFrom) แต่ค่าที่ส่งขึ้นไปให้ parent ผ่าน callback onTotal
// เปลี่ยนเป็นนับเฉพาะรายการตั้งแต่ dateFrom เป็นต้นไปแทน (ใช้ป้อนการ์ด "สรุปยอดยืม-คืน"
// ที่ต้องการยอด "ตั้งแต่กำหนดยอดคงเหลือ" ไม่ใช่สะสมทั้งหมด) — ถ้ายังไม่เคยกำหนดยอด
// คงเหลือเลย (dateFrom ว่าง) ค่าที่ส่งขึ้นไปจะเท่ากับยอดสะสมทั้งหมดเหมือนเดิม
function groupRowsByPayment(rows) {
  const byPaymentId = new Map();
  rows.forEach((row) => {
    const key = row.payment_id;
    if (!byPaymentId.has(key)) {
      byPaymentId.set(key, {
        payment_id: row.payment_id,
        payment_number: row.payment_number,
        date: row.date,
        rawOcrVerified: !!row.ocr_verified_loan_pair,
        items: [],
        amount: 0,
      });
    }
    const group = byPaymentId.get(key);
    group.items.push(row);
    group.amount += Number(row.amount || 0);
  });
  return [...byPaymentId.values()].sort((a, b) => new Date(b.date) - new Date(a.date));
}

export function LoanProjectExpenseList({
  customerId, projectName, projectNumber, label, onTotal, onUnverifiedTotal, dateFrom, showStatusColumn,
  unverifiedOnly, showOcrButton, ocrEnabled = true,
}) {
  const dispatch = useDispatch();
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [unverifiedAmount, setUnverifiedAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ocrDialogOpen, setOcrDialogOpen] = useState(false);

  // ocrEnabled=false (toggle ต่อคู่ยืม-คืน) — ข้าม logic OCR ทั้งหมด ถือว่าทุกรายการยืนยันแล้ว
  const isVerified = useCallback(
    (group) => !ocrEnabled || (dateFrom && new Date(group.date) < new Date(dateFrom)) || group.rawOcrVerified,
    [dateFrom, ocrEnabled],
  );

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError('');
    try {
      const data = await dispatch(actions.listLoanProjectExpenses({ customerId, projectName, projectNumber }));
      const grouped = groupRowsByPayment(data.rows);
      setGroups(grouped);
      setTotal(data.total);
      if (onTotal) {
        const sinceCutoffTotal = dateFrom
          ? grouped
            .filter((group) => new Date(group.date) >= new Date(dateFrom))
            .reduce((sum, group) => sum + group.amount, 0)
          : data.total;
        onTotal(sinceCutoffTotal);
      }
      const unverifiedSum = grouped
        .filter((group) => !isVerified(group))
        .reduce((sum, group) => sum + group.amount, 0);
      setUnverifiedAmount(unverifiedSum);
      if (onUnverifiedTotal) onUnverifiedTotal(unverifiedSum);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, projectName, projectNumber, dateFrom, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const displayGroups = unverifiedOnly ? groups.filter((group) => !isVerified(group)) : groups;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {(label || showOcrButton) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          {label && <Typography variant="subtitle2">{label}</Typography>}
          {showOcrButton && (
            <Button size="small" variant="outlined" onClick={() => setOcrDialogOpen(true)}>
              อัปโหลดรูปยืนยัน OCR
            </Button>
          )}
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {loading ? <CircularProgress size={24} /> : (
        <>
          {displayGroups.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {unverifiedOnly ? 'ยืนยันครบทุกรายการแล้ว' : 'ไม่มีรายการ'}
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
              {displayGroups.map((group) => (
                <Accordion key={group.payment_id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                      <Typography variant="body2">
                        {group.payment_number} — {new Date(group.date).toLocaleDateString('th-TH')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {showStatusColumn && (
                          isVerified(group) ? (
                            <Chip size="small" color="success" label="ยืนยันแล้ว" />
                          ) : (
                            <Chip size="small" color="warning" label="ยังไม่ยืนยัน" />
                          )
                        )}
                        <Typography variant="body2">{formatCurrency(group.amount)}</Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>รายการ</TableCell>
                          <TableCell align="right">จำนวนเงิน</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.items.map((item, idx) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <TableRow key={idx}>
                            <TableCell>{item.name || '-'}</TableCell>
                            <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>รวม</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatCurrency(unverifiedOnly ? unverifiedAmount : total)} บาท
            </Typography>
          </Box>
        </>
      )}

      {showOcrButton && (
        <LoanSlipVerifyDialog
          open={ocrDialogOpen}
          onClose={() => setOcrDialogOpen(false)}
          customerId={customerId}
          projectName={projectName}
          projectNumber={projectNumber}
          dateFrom={dateFrom}
          systemPayments={displayGroups}
          onConfirmed={load}
        />
      )}
    </Paper>
  );
}

export default LoanProjectExpenseList;
